import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { db, paginate, searchRows, sortRows, uid } from "./db";

interface Args {
  url: string;
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: any;
  params?: Record<string, any>;
}

const wait = (ms = 250) => new Promise((r) => setTimeout(r, ms));

type Collection = keyof typeof db;

const searchFields: Partial<Record<Collection, string[]>> = {
  students: ["name", "email", "mobile"],
  years: ["name", "description"],
  subjects: ["name", "description"],
  chapters: ["name", "description"],
  videos: ["title"],
  notes: ["title", "description"],
  chapterTests: ["title"],
  weeklyExams: ["title"],
  questions: ["text"],
};

export const mockBaseQuery: BaseQueryFn<Args, unknown, { message: string }> = async ({
  url,
  method = "GET",
  body,
  params,
}) => {
  await wait(200);
  // url format: /collection or /collection/:id
  const [, collection, id] = url.split("/");
  const coll = collection as Collection;
  if (!db[coll]) return { error: { message: `Unknown collection ${collection}` } };

  const list = db[coll] as any[];

  if (method === "GET") {
    if (id) {
      const item = list.find((r) => r.id === id);
      return item ? { data: item } : { error: { message: "Not found" } };
    }
    let rows = [...list];
    // Filters
    if (params) {
      const { search, sortBy, order, page, limit, ...filters } = params;
      Object.entries(filters).forEach(([k, v]) => {
        if (v == null || v === "" || v === "all") return;
        if (Array.isArray(v)) {
          rows = rows.filter((r) => v.includes(r[k]));
        } else {
          rows = rows.filter((r) => r[k] === v);
        }
      });
      if (search) rows = searchRows(rows, search, (searchFields[coll] ?? []) as any);
      rows = sortRows(rows, sortBy ?? "createdAt", (order as any) ?? "desc");
      const p = Number(page ?? 1);
      const l = Number(limit ?? 10);
      return { data: paginate(rows, p, l) };
    }
    rows = sortRows(rows, "createdAt", "desc");
    return { data: paginate(rows, 1, 10) };
  }

  if (method === "POST") {
    const item = { id: uid(), createdAt: new Date().toISOString(), ...body };
    list.unshift(item);
    return { data: item };
  }

  if (method === "PATCH") {
    const idx = list.findIndex((r) => r.id === id);
    if (idx === -1) return { error: { message: "Not found" } };
    list[idx] = { ...list[idx], ...body };
    return { data: list[idx] };
  }

  if (method === "DELETE") {
    const idx = list.findIndex((r) => r.id === id);
    if (idx === -1) return { error: { message: "Not found" } };
    const [removed] = list.splice(idx, 1);
    return { data: removed };
  }

  return { error: { message: "Bad request" } };
};

export interface ListResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}
